import { Suspense, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { ArrowLeft, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { useNavigation } from '@/hooks/useNavigation';
import { RouteProgress } from '@/components/ui/RouteProgress';
import { Skeleton } from '@/components/ui/Feedback';
import { navIcon } from './navIcons';

/**
 * Persona theme. The receiver app is green/blue, the dispatcher app is violet —
 * exactly as in the two mobile mockups. Everything accent-coloured reads from
 * here so a screen never hard-codes a persona colour.
 */
export type MobileTheme = 'receive' | 'dispatch';

export const MOBILE_THEME: Record<
  MobileTheme,
  { accent: string; accentBg: string; accentText: string; accentSoft: string; ring: string }
> = {
  receive: {
    accent: 'bg-brand-600 hover:bg-brand-700',
    accentBg: 'bg-brand-600',
    accentText: 'text-brand-600',
    accentSoft: 'bg-brand-50',
    ring: 'ring-brand-600',
  },
  dispatch: {
    accent: 'bg-dispatch-600 hover:bg-dispatch-700',
    accentBg: 'bg-dispatch-600',
    accentText: 'text-dispatch-600',
    accentSoft: 'bg-dispatch-50',
    ring: 'ring-dispatch-600',
  },
};

/**
 * Wraps the scanner screens in a phone-sized column. On a real handset it is
 * simply full-bleed; on a desktop browser it is centred with a device frame so
 * the flows can be reviewed without a phone.
 */
export function MobileLayout({ theme: themeOverride }: { theme?: MobileTheme }) {
  const persona = useAuthStore((state) => state.user?.mobilePersona);
  const { data } = useNavigation();

  // The shared screens (history, profile) omit `theme` and follow the persona the
  // backend assigned to this role, so a dispatcher never sees the receiver's bar.
  const theme = themeOverride ?? (persona === 'DISPATCH' ? 'dispatch' : 'receive');
  // Tabs come from GET /navigation, filtered by permission exactly like the
  // desktop sidebar — a role without CARTON_RECEIVE simply has no Scan tab.
  const tabs = data?.mobile ?? [];

  return (
    <div className="flex min-h-dvh justify-center bg-canvas sm:items-center sm:py-8">
      <RouteProgress />
      <div
        className={cn(
          'relative flex min-h-dvh w-full flex-col bg-white',
          'sm:min-h-0 sm:h-[860px] sm:max-w-[420px] sm:overflow-hidden sm:rounded-[34px] sm:border sm:border-line sm:shadow-pop',
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-[76px]">
          <Suspense fallback={<MobileScreenFallback />}>
            <Outlet />
          </Suspense>
        </div>

        <nav
          className="absolute inset-x-0 bottom-0 z-10 flex items-stretch border-t border-line bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur"
          aria-label="App sections"
        >
          {tabs.map((tab) => {
            const Icon = navIcon(tab.icon);
            return (
              <NavLink
                key={tab.code}
                to={tab.path ?? '#'}
                end={tab.exact}
                className={({ isActive }) =>
                  cn(
                    'flex flex-1 flex-col items-center gap-1 py-2.5 text-[10.5px] font-semibold transition-colors',
                    isActive ? MOBILE_THEME[theme].accentText : 'text-ink-muted hover:text-ink-soft',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className={cn('size-5', isActive && 'stroke-[2.4]')} aria-hidden />
                    {tab.label}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

/** Phone-shaped silhouette shown while a scanner screen's chunk is fetched. */
function MobileScreenFallback() {
  return (
    <div className="flex flex-col gap-4 px-5 pt-6" role="status" aria-label="Loading screen">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="size-10 shrink-0 rounded-xl" />
      </div>
      <Skeleton className="h-[120px] w-full rounded-2xl" />
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-20 rounded-2xl" />
        <Skeleton className="h-20 rounded-2xl" />
      </div>
      <Skeleton className="h-4 w-28" />
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className="h-16 w-full rounded-2xl" />
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Screen chrome                                                              */
/* -------------------------------------------------------------------------- */

/** Standard app bar: back chevron, centred title, optional trailing action. */
export function MobileHeader({
  title,
  backTo,
  onBack,
  action,
  className,
}: {
  title: string;
  backTo?: string;
  onBack?: () => void;
  action?: ReactNode;
  className?: string;
}) {
  const navigate = useNavigate();
  const showBack = Boolean(backTo || onBack);

  return (
    <header
      className={cn(
        'sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b border-line bg-white/95 px-3 backdrop-blur',
        className,
      )}
    >
      {showBack ? (
        <button
          type="button"
          aria-label="Go back"
          onClick={() => (onBack ? onBack() : navigate(backTo!))}
          className="inline-flex size-9 items-center justify-center rounded-lg text-ink-soft transition-colors hover:bg-canvas"
        >
          <ArrowLeft className="size-5" aria-hidden />
        </button>
      ) : (
        <span className="size-9" aria-hidden />
      )}

      <h1 className="flex-1 truncate text-center text-[15px] font-bold text-ink">{title}</h1>

      <span className="inline-flex size-9 items-center justify-center">{action}</span>
    </header>
  );
}

/** Greeting header used on the two mobile dashboards. */
export function MobileGreeting({
  name,
  roleLabel,
  onNotifications,
}: {
  name: string;
  roleLabel: string;
  onNotifications?: () => void;
}) {
  return (
    <header className="flex shrink-0 items-start justify-between gap-3 px-5 pt-6 pb-4">
      <div className="min-w-0">
        <p className="truncate text-lg font-bold text-ink">
          Hello, {name.split(' ')[0]} <span aria-hidden>👋</span>
        </p>
        <p className="mt-0.5 text-[13px] text-ink-muted">{roleLabel}</p>
      </div>
      <button
        type="button"
        aria-label="Notifications"
        onClick={onNotifications}
        className="relative inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-line text-ink-soft transition-colors hover:bg-canvas"
      >
        <Bell className="size-[18px]" aria-hidden />
        <span className="absolute top-2 right-2.5 size-1.5 rounded-full bg-red-500" aria-hidden />
      </button>
    </header>
  );
}

/** Signed-in persona label, used by the greeting and the profile screen. */
export function useMobilePersona() {
  const user = useAuthStore((state) => state.user);
  return user;
}
